import React, { useState } from "react";
import { type DPCalendar, useContextDatePickerOffsetPropGetters, useContextYearsPropGetters, useDatePickerStateContext } from "@rehookify/datepicker";
import isSameDay from "date-fns/isSameDay";

import { SupChevronLeft, SupChevronRight } from "../../../../icons";

import { DayPicker } from "./DayPicker";
import { MonthPicker } from "./MonthPicker";
import { YearPicker } from "./YearPicker";
import { TimePicker } from "./TimePicker";
import { Header } from "./Header";

type PickerType = "day" | "month" | "year" | "time";

interface CalendarProps {
  displayTimePicker?: boolean;
  data: DPCalendar;
  onClose?: () => void;
  onChange?: (date?: Date) => void;
}

export function Calendar({ displayTimePicker = false, data, onClose, onChange }: CalendarProps) {
  const [pickerType, setPickerType] = useState<PickerType>("day");
  const { selectedDates } = useDatePickerStateContext();
  const { subtractOffset, addOffset } = useContextDatePickerOffsetPropGetters();
  const { nextYearsButton, previousYearsButton } = useContextYearsPropGetters();

  function handleDateChange(date?: Date) {
    setPickerType("day");
    onChange?.(date);
  }

  function handleClose(date?: Date) {
    if (date && isSameDay(selectedDates[0], date)) return;

    if (displayTimePicker) {
      setPickerType("time");
    } else {
      onClose?.();
    }
  }

  function getNextButtonProps() {
    switch (pickerType) {
      case "time":
        return addOffset({ days: 1 });
      case "day":
        return addOffset({ months: 1 });
      case "month":
        return addOffset({ years: 1 });
      case "year":
        return nextYearsButton();
      default:
        return addOffset({ months: 1 });
    }
  }

  function getPrevButtonProps() {
    switch (pickerType) {
      case "time":
        return subtractOffset({ days: 1 });
      case "day":
        return subtractOffset({ months: 1 });
      case "month":
        return subtractOffset({ years: 1 });
      case "year":
        return previousYearsButton();
      default:
        return subtractOffset({ months: 1 });
    }
  }

  const nextButton = (
    <button {...getNextButtonProps()}>
      <SupChevronRight />
    </button>
  );

  const prevButton = (
    <button {...getPrevButtonProps()}>
      <SupChevronLeft />
    </button>
  );

  return (
    <div className="sup-calendar text-neutral-900">
      <Header prevButton={prevButton} nextButton={nextButton} onToggle={setPickerType} />

      {pickerType === "day" && <DayPicker days={data.days} onDayPick={handleClose} />}
      {pickerType === "month" && <MonthPicker onMonthPick={handleDateChange} />}
      {pickerType === "year" && <YearPicker onYearPick={handleDateChange} />}
      {pickerType === "time" && <TimePicker onTimePick={onClose} />}
    </div>
  );
}
